import { Stack } from '@amboss/design-system';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { config } from '../../../config';
import { useAppSelector } from '../../../hooks/redux';
import useProfile from '../../../hooks/useProfile';
import { useSocketEmit } from '../../../hooks/useSocketEmit';
import type { AccountSetupFields } from '../../../interfaces/accountSetupFields';
import { setProfileLoaded } from '../../../store/profile/profileSlice';
import { normalizeProfileParams } from '../../../utils';
import { validateProfileSubmit } from '../../../validators/validators';
import CmeCpdCe from './bits/CmeCpdCe';
import Footer from './bits/Footer';
import Heading from './bits/Heading';
import PersonalInformation from './bits/PersonalInformation';
import Sections from './bits/Sections';
import SpecialtyState from './bits/SpecialtyState';

const DEFAULT_VALUES: AccountSetupFields = {
  name: '',
  lastName: '',
  email: '',
  birthDate: '',
  graduationDate: '',
  boards: [],
  clinicalRole: '',
  clinicalRoleValue: '',
  association: '',
};

declare type ProfileEditProps = {
  callback: () => void;
  'data-testid'?: string;
};
const ProfileEdit = ({ callback }: ProfileEditProps) => {
  const { t } = useTranslation();
  const [initialValues, setInitialValues] = useState<AccountSetupFields>(DEFAULT_VALUES);
  const [loading, setLoading] = useState(true);
  const profileLoaded = useAppSelector((state) => state.profile.profileLoaded);

  const { refreshProfile, profile } = useProfile();
  const { saveProfile } = useSocketEmit();

  useEffect(() => {
    refreshProfile();
  }, []);

  useEffect(() => {
    setLoading(false);
  }, [initialValues]);

  useEffect(() => {
    if (profileLoaded) {
      setInitialValues({
        name: profile?.name || '',
        lastName: profile?.lastName || '',
        email: profile?.email || '',
        birthDate: profile?.birthDate || '',
        graduationDate: profile?.graduationDate || '',
        boards: profile?.boards || [],
        clinicalRole: profile?.clinicalRole?.key || '',
        clinicalRoleValue: profile?.clinicalRole?.value || '',
        association: profile?.association || config.forms.cpd.types.AMA,
      });
    }
    return () => {
      setLoading(true);
    };
  }, [profileLoaded, profile]);

  const heading =
    !profile || !profile.name ? (
      <Heading text={t('accountSetup.sections.header.profileEdit') as string} />
    ) : null;

  return (
    <>
      {!loading && (
        <Formik
          initialValues={initialValues}
          validate={validateProfileSubmit}
          validateOnMount={true}
          onSubmit={(values) => {
            const updatedValues = normalizeProfileParams(values);
            saveProfile(updatedValues)
              .then(() => callback())
              .catch(() => {
                setProfileLoaded(false);
                alert(t('accountSetup.sections.personalInformation.form.submitError'));
              });
          }}
          onReset={callback}
        >
          <Form>
            <Sections>
              {heading}
              <Stack space={'xxl'}>
                <PersonalInformation />
                <SpecialtyState />
                <CmeCpdCe />
                <Footer />
              </Stack>
            </Sections>
          </Form>
        </Formik>
      )}
    </>
  );
};

export default ProfileEdit;
